/**
 * Generic field/operator/value condition stack, extracted from the Item Scorer
 * Summary filter bar so other tables (item bank, reports) can reuse the same
 * chip UI and matching semantics.
 *
 * Record-agnostic on purpose: each host supplies a field catalog whose `value`
 * accessor pulls the comparable value off its own row shape. Kept free of
 * Angular so the composition is unit-testable on its own.
 */

export type ConditionJoinMode = 'AND' | 'OR';

export type ConditionOp =
  | 'eq' | 'neq'
  | 'contains' | 'notContains'
  | 'gte' | 'gt' | 'lte' | 'lt' | 'between' | 'outside'
  | 'empty' | 'notEmpty'
  | 'in' | 'notIn'
  | 'is';

export interface ConditionOpDef {
  id: ConditionOp;
  /** Long form for the operator picker. */
  label: string;
  /** Short form for chips and the criteria line. */
  symbol: string;
  /** How many values the operator consumes. */
  args: 0 | 1 | 2;
}

export const CONDITION_OPS: { [id: string]: ConditionOpDef } = {
  eq:          { id: 'eq',          label: 'is',              symbol: '=',           args: 1 },
  neq:         { id: 'neq',         label: 'is not',          symbol: '≠',           args: 1 },
  contains:    { id: 'contains',    label: 'contains',        symbol: 'contains',    args: 1 },
  notContains: { id: 'notContains', label: 'does not contain',symbol: '!contains',   args: 1 },
  gte:         { id: 'gte',         label: 'is at least',     symbol: '≥',           args: 1 },
  gt:          { id: 'gt',          label: 'is more than',    symbol: '>',           args: 1 },
  lte:         { id: 'lte',         label: 'is at most',      symbol: '≤',           args: 1 },
  lt:          { id: 'lt',          label: 'is less than',    symbol: '<',           args: 1 },
  between:     { id: 'between',     label: 'is between',      symbol: 'between',     args: 2 },
  outside:     { id: 'outside',     label: 'is outside',      symbol: 'outside',     args: 2 },
  empty:       { id: 'empty',       label: 'is blank',        symbol: 'is blank',    args: 0 },
  notEmpty:    { id: 'notEmpty',    label: 'is not blank',    symbol: 'has a value', args: 0 },
  in:          { id: 'in',          label: 'is any of',       symbol: 'any of',      args: 1 },
  notIn:       { id: 'notIn',       label: 'is none of',      symbol: 'none of',     args: 1 },
  is:          { id: 'is',          label: 'is',              symbol: 'is',          args: 1 },
};

export type ConditionFieldType = 'number' | 'text' | 'select' | 'flag';

export interface ConditionFieldOption {
  id: string;
  label?: string;
}

export interface ConditionFieldDef {
  id: string;
  label: string;
  type: ConditionFieldType;
  unit?: 'percent' | 'count';
  /** Overrides DEFAULT_OPS_BY_TYPE when a field needs a narrower set. */
  ops?: ConditionOp[];
  /** Choices for `select` fields; also used to offer suggestions on `text`. */
  options?: ConditionFieldOption[];
  /** Pulls the comparable value off a row. */
  value: (record: any) => any;
  hint?: string;
  /** Groups fields in the picker, e.g. 'Item', 'Parameters'. */
  group?: string;
}

export const DEFAULT_OPS_BY_TYPE: { [type: string]: ConditionOp[] } = {
  number: ['gte', 'gt', 'lte', 'lt', 'eq', 'between', 'outside', 'empty', 'notEmpty'],
  text:   ['eq', 'neq', 'contains', 'notContains', 'in', 'notIn', 'empty', 'notEmpty'],
  select: ['eq', 'neq', 'in', 'notIn', 'empty', 'notEmpty'],
  flag:   ['is'],
};

export interface FilterCondition {
  /** Client-side identity so the UI can track chips across edits. */
  id: string;
  field: string | null;
  op: ConditionOp | null;
  value?: any;
  value2?: any;
}

export interface ConditionFilterState {
  joinMode?: ConditionJoinMode;
  conditions?: FilterCondition[];
}

export interface ConditionFilterResult<T = any> {
  rows: T[];
  shown: number;
  total: number;
  /** e.g. `Strand = Number Sense AND FT is yes` */
  expression: string;
}

let conditionSeq = 0;

export function newCondition(field: string | null = null): FilterCondition {
  conditionSeq++;
  return { id: `cond_${conditionSeq}`, field, op: null, value: null, value2: null };
}

export function conditionFieldById(fields: ConditionFieldDef[], id: string): ConditionFieldDef {
  return (fields || []).find((field) => field.id === id);
}

export function opsForField(field: ConditionFieldDef): ConditionOpDef[] {
  if (!field) {
    return [];
  }
  const ids = field.ops && field.ops.length ? field.ops : DEFAULT_OPS_BY_TYPE[field.type] || [];
  return ids.map((id) => CONDITION_OPS[id]).filter(Boolean);
}

/** Parity with ItemBankUtilCtrl.numStrToBool used in item listing filters. */
export function normalizeCompareValue(val: unknown): unknown {
  if (val === undefined || val === null) {
    return false;
  }
  if (val === '0' || val === 0) {
    return false;
  }
  if (val === '1' || val === 1) {
    return true;
  }
  if (typeof val === 'boolean') {
    return val;
  }
  if (typeof val === 'string') {
    const normalized = val.trim().toLowerCase();
    if (normalized === 'false' || normalized === '0') {
      return false;
    }
    if (normalized === 'true' || normalized === '1') {
      return true;
    }
    return val.trim();
  }
  return val;
}

export function valueIsPresent(val: unknown): boolean {
  if (val === null || val === undefined) {
    return false;
  }
  if (typeof val === 'string') {
    return val.trim() !== '';
  }
  if (Array.isArray(val)) {
    return val.length > 0;
  }
  return true;
}

const asComparableString = (val: unknown): string =>
  String(normalizeCompareValue(val)).trim().toLowerCase();

/** Exact match after bool/num normalization — no substring widening. */
export function valuesEqual(a: unknown, b: unknown): boolean {
  if (a === b) {
    return true;
  }
  return asComparableString(a) === asComparableString(b);
}

export function valueContains(haystack: unknown, needle: unknown): boolean {
  const left = asComparableString(haystack);
  const right = asComparableString(needle);
  return !!right && left.includes(right);
}

/** `in` / `notIn` accept a comma-separated list or an array. */
export function parseValueList(val: unknown): string[] {
  const raw = Array.isArray(val) ? val : String(val ?? '').split(',');
  return raw.map((entry) => String(entry ?? '').trim()).filter((entry) => entry !== '');
}

const isNumericArg = (val: any): boolean =>
  val != null && val !== '' && Number.isFinite(Number(val));

/** A condition filters nothing until its field, operator and arguments are all set. */
export function isConditionComplete(
  fields: ConditionFieldDef[],
  condition: FilterCondition,
): boolean {
  const field = condition && condition.field ? conditionFieldById(fields, condition.field) : null;
  if (!field || !condition.op) {
    return false;
  }
  const op = CONDITION_OPS[condition.op];
  if (!op) {
    return false;
  }
  if (opsForField(field).findIndex((candidate) => candidate.id === condition.op) === -1) {
    return false;
  }
  if (field.type === 'flag') {
    return condition.value === true || condition.value === false;
  }
  if (op.args === 0) {
    return true;
  }
  if (condition.op === 'in' || condition.op === 'notIn') {
    return parseValueList(condition.value).length > 0;
  }
  if (field.type === 'number') {
    if (op.args === 2) {
      return isNumericArg(condition.value) && isNumericArg(condition.value2);
    }
    return isNumericArg(condition.value);
  }
  if (op.args === 2) {
    return valueIsPresent(condition.value) && valueIsPresent(condition.value2);
  }
  return valueIsPresent(condition.value);
}

export function evaluateCondition(
  record: any,
  fields: ConditionFieldDef[],
  condition: FilterCondition,
): boolean {
  const field = conditionFieldById(fields, condition.field);
  if (!field) {
    return true;
  }
  const raw = field.value(record);

  if (condition.op === 'empty') {
    return !valueIsPresent(raw);
  }
  if (condition.op === 'notEmpty') {
    return valueIsPresent(raw);
  }

  if (field.type === 'flag') {
    // An unset flag reads as "no", matching numStrToBool on a missing param.
    if (!valueIsPresent(raw)) {
      return condition.value === false;
    }
    return normalizeCompareValue(raw) === !!condition.value;
  }

  if (condition.op === 'in') {
    return parseValueList(condition.value).some((entry) => valuesEqual(raw, entry));
  }
  if (condition.op === 'notIn') {
    return !parseValueList(condition.value).some((entry) => valuesEqual(raw, entry));
  }
  if (condition.op === 'contains') {
    return valueContains(raw, condition.value);
  }
  if (condition.op === 'notContains') {
    return !valueContains(raw, condition.value);
  }

  if (field.type === 'number') {
    // Number(null) and Number('') are both 0, so a blank would otherwise clear
    // any threshold at or below zero. A field with no data satisfies nothing.
    if (!valueIsPresent(raw)) {
      return false;
    }
    const value = Number(raw);
    if (!Number.isFinite(value)) {
      return false;
    }
    const a = Number(condition.value);
    const b = Number(condition.value2);
    switch (condition.op) {
      case 'gte':     return value >= a;
      case 'gt':      return value > a;
      case 'lte':     return value <= a;
      case 'lt':      return value < a;
      // Percents come from ratio * 100, so 0.55 lands on 55.00000000000001.
      case 'eq':      return Math.abs(value - a) < 1e-9;
      case 'neq':     return Math.abs(value - a) >= 1e-9;
      case 'between': return value >= Math.min(a, b) && value <= Math.max(a, b);
      case 'outside': return value < Math.min(a, b) || value > Math.max(a, b);
      default:        return true;
    }
  }

  if (condition.op === 'eq' || condition.op === 'is') {
    return valuesEqual(raw, condition.value);
  }
  if (condition.op === 'neq') {
    return !valuesEqual(raw, condition.value);
  }
  return true;
}

export function describeCondition(
  fields: ConditionFieldDef[],
  condition: FilterCondition,
): string {
  const field = conditionFieldById(fields, condition.field);
  if (!field || !condition.op) {
    return '';
  }
  const op = CONDITION_OPS[condition.op];
  if (!op) {
    return '';
  }
  if (field.type === 'flag') {
    return `${field.label} is ${condition.value ? 'yes' : 'no'}`;
  }
  const unit = field.unit === 'percent' ? '%' : '';
  if (op.args === 0) {
    return `${field.label} ${op.symbol}`;
  }
  if (condition.op === 'in' || condition.op === 'notIn') {
    return `${field.label} ${op.symbol} ${parseValueList(condition.value).join(', ')}`;
  }
  if (op.args === 2) {
    return `${field.label} ${op.symbol} ${condition.value}${unit} and ${condition.value2}${unit}`;
  }
  return `${field.label} ${op.symbol} ${condition.value}${unit}`;
}

export function completeConditions(
  fields: ConditionFieldDef[],
  conditions: FilterCondition[],
): FilterCondition[] {
  return (conditions || []).filter((condition) => isConditionComplete(fields, condition));
}

export function recordMatchesConditions(
  record: any,
  fields: ConditionFieldDef[],
  state: ConditionFilterState = {},
): boolean {
  const active = completeConditions(fields, state.conditions);
  if (!active.length) {
    return true;
  }
  const results = active.map((condition) => evaluateCondition(record, fields, condition));
  return state.joinMode === 'OR' ? results.some(Boolean) : results.every(Boolean);
}

/** Run the whole stack and report both the surviving rows and what narrowed them. */
export function applyConditionStack<T = any>(
  records: T[],
  fields: ConditionFieldDef[],
  state: ConditionFilterState = {},
): ConditionFilterResult<T> {
  const all = records || [];
  const active = completeConditions(fields, state.conditions);
  const joinMode: ConditionJoinMode = state.joinMode === 'OR' ? 'OR' : 'AND';
  const rows = active.length
    ? all.filter((record) => recordMatchesConditions(record, fields, { joinMode, conditions: active }))
    : [...all];
  return {
    rows,
    shown: rows.length,
    total: all.length,
    expression: active
      .map((condition) => describeCondition(fields, condition))
      .filter(Boolean)
      .join(` ${joinMode} `),
  };
}
